/**
 * Given a Firestore document snapshot (the result of path/ID lookup in Firestore) checks whether a document exists in
 * that snapshot. If no document exists (the lookup failed), it either throws an exception, or returns some custom value
 *
 * For example:
 *
 * const snapshot = await db.user(userHandle).get();
 * const user: IUserData = oneFromSnapshot(snapshot); // or throws if user does not exist
 *
 * Or, with a custom orElse:
 *
 * const snapshot = await db.user(userHandle).get();
 * const maybeUser: IUserData | undefined = oneFromSnapshot(snapshot, () => undefined);
 *
 * @param snapshot The Firestore document snapshot to check - its .data() is returned if the snapshot exists (i.e. if
 *                  the lookup was successful)
 * @param orElse A function that returns a custom value, used if the snapshot doesn't exist. The default orElse throws
 *               an Error, so if you don't provide a custom orElse, this function will narrow the type of the return
 *               value to the type of the snapshot's data
 * @throws Error If the snapshot doesn't exist or is undefined, and a custom orElse was not provided
 */
export function oneFromSnapshot(snapshot, orElse = () => {
    throw new Error("Expected one result from Firestore snapshot; received undefined");
}) {
    const result = snapshot?.data();
    if (result === undefined) {
        return orElse();
    }
    return result;
}
/**
 * Given a Firestore query snapshot (the result of a Firestore query), gets the first document from that query snapshot,
 * if any results were returned. If the query returned no results, it either throws an exception, or returns some custom
 * value
 *
 * It's a good idea to use .limit(1) on the query before passing the result to this method - because only the first resulting
 * document will be used by this call.
 *
 * For example:
 *
 * const snapshot = await db.userCollection().where("uid", "==", uid).limit(1).get();
 * const user: IUserData = firstFromSnapshot(snapshot); // or throws if user was not found
 *
 * Or, with a custom orElse:
 *
 * const snapshot = await db.userCollection().where("uid", "==", uid).limit(1).get();
 * const maybeUser: IUserData | undefined = firstFromSnapshot(snapshot, () => undefined);
 *
 * @param snapshot The Firestore document snapshot to check - its .data() is returned if the snapshot exists (i.e. if
 *                  the lookup was successful)
 * @param orElse A function that returns a custom value, used if the query returned no results. The default orElse throws
 *                an Error, so if you don't provide a custom orElse, this function will narrow the type of the return
 *                value to the type of the query's data
 * @throws Error  If the query returned no results, and a custom orElse was not provided
 */
export function firstFromSnapshot(snapshot, orElse = () => {
    throw new Error("Expected one result from Firestore snapshot; received undefined");
}) {
    const result = snapshot.docs?.[0]?.data();
    if (result === undefined) {
        return orElse();
    }
    return result;
}
/**
 * Gets all the documents from a query snapshot, returning them as an array
 *
 * If no results were matched by the query, the array will be empty (but note that [] is truthy)
 */
export function allFromSnapshot(snapshot) {
    return snapshot.docs.map((r) => r.data());
}
/**
 * Gets all the documents from a query snapshot, and returns them indexed by their .uid field
 *
 * If no results were matched by the query, the object will have no entries (but note that {} is truthy)
 */
export function allFromSnapshotByUid(snapshot) {
    return Object.fromEntries(allFromSnapshot(snapshot).map((r) => [r.uid, r]));
}
//# sourceMappingURL=helpers.js.map